import { useEffect, useRef, useState } from "react";
import { Eraser, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SignaturePad({ onChange, disabled = false, value = null }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const ink = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
    // restore a previously captured signature (e.g. after navigating Back in a wizard)
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        ink.current = true;
        setHasInk(true);
      };
      img.src = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    try {
      canvasRef.current.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture unsupported — drawing still works */
    }
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!ink.current) {
      ink.current = true;
      setHasInk(true);
    }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (ink.current) onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    ink.current = false;
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div className="space-y-2" data-testid="signature-pad">
      <div className="relative">
        <canvas
          ref={canvasRef}
          data-testid="signature-pad-canvas"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          className={`h-40 w-full touch-none rounded-md border border-input bg-white ${disabled ? "opacity-50" : "cursor-crosshair"}`}
          aria-label="Signature pad — draw your signature here"
          role="img"
        />
        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-slate-400">
            <PenLine className="h-4 w-4" />
            <span className="text-sm">Sign here with finger, stylus or mouse</span>
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="signature-pad-clear"
          onClick={clear}
          disabled={disabled || !hasInk}
          className="h-9 text-xs"
        >
          <Eraser className="h-3.5 w-3.5 mr-1" /> Clear signature
        </Button>
      </div>
    </div>
  );
}
